/**
 * Boolean engine — Manifold (WASM) behind a MeshData-in/MeshData-out API.
 *
 * Manifold requires watertight, merged input; our kernel meshes duplicate
 * vertices for flat shading, so we round-trip: merge exact-duplicate verts
 * into topology (Mesh.merge) → boolean → re-expand to flat-shaded soup with
 * sharp display edges. The WASM module (~1MB) lazy-loads on first boolean.
 */

import Module from 'manifold-3d';
import type { Manifold, ManifoldToplevel } from 'manifold-3d';

import { sharpEdges } from './meshUtils';
import { soupToMeshData } from '../io/mesh';
import type { MeshData } from './types';

export type BoolOp = 'join' | 'cut' | 'intersect';

let modulePromise: Promise<ManifoldToplevel> | null = null;

async function getModule(): Promise<ManifoldToplevel> {
  if (!modulePromise) {
    modulePromise = Module().then((wasm) => {
      wasm.setup();
      return wasm;
    });
  }
  return modulePromise;
}

function toManifold(wasm: ManifoldToplevel, mesh: MeshData): Manifold {
  const vertProperties = new Float32Array(mesh.positions);
  const triVerts = new Uint32Array(mesh.indices);
  const m = new wasm.Mesh({ numProp: 3, vertProperties, triVerts });
  m.merge(); // stitch exact-duplicate vertices into watertight topology
  return new wasm.Manifold(m);
}

function fromManifold(manifold: Manifold): MeshData {
  const mesh = manifold.getMesh();
  const positions = mesh.vertProperties as Float32Array;
  const indices = mesh.triVerts as Uint32Array;
  // Display edges come from merged topology; shading from re-expanded soup.
  const edges = sharpEdges(positions, indices);
  const soup = new Float32Array(indices.length * 3);
  for (let i = 0; i < indices.length; i++) {
    const v = indices[i]! * 3;
    soup[i * 3] = positions[v]!;
    soup[i * 3 + 1] = positions[v + 1]!;
    soup[i * 3 + 2] = positions[v + 2]!;
  }
  const out = soupToMeshData(soup);
  return { ...out, edgePositions: edges };
}

/**
 * Apply `tool` to every target with the given op and return the surviving
 * geometry as one MeshData (targets stay distinct manifolds internally).
 * Throws with a user-readable message on non-manifold input.
 */
export async function applyBoolean(
  op: BoolOp,
  targets: readonly MeshData[],
  tool: MeshData,
): Promise<MeshData> {
  const wasm = await getModule();
  const created: Manifold[] = [];
  try {
    const toolM = toManifold(wasm, tool);
    created.push(toolM);
    const pieces: MeshData[] = [];

    if (op === 'join') {
      let acc = toolM;
      for (const t of targets) {
        const tm = toManifold(wasm, t);
        created.push(tm);
        const next = acc.add(tm);
        created.push(next);
        acc = next;
      }
      pieces.push(fromManifold(acc));
    } else {
      for (const t of targets) {
        const tm = toManifold(wasm, t);
        created.push(tm);
        const result = op === 'cut' ? tm.subtract(toolM) : tm.intersect(toolM);
        created.push(result);
        if (result.numTri() > 0) pieces.push(fromManifold(result));
      }
    }

    // Concatenate surviving pieces into one display mesh.
    const total = pieces.reduce(
      (acc, p) => ({
        pos: acc.pos + p.positions.length,
        idx: acc.idx + p.indices.length,
        edge: acc.edge + p.edgePositions.length,
      }),
      { pos: 0, idx: 0, edge: 0 },
    );
    const positions = new Float32Array(total.pos);
    const normals = new Float32Array(total.pos);
    const indices = new Uint32Array(total.idx);
    const edgePositions = new Float32Array(total.edge);
    let pOff = 0, iOff = 0, eOff = 0;
    for (const p of pieces) {
      positions.set(p.positions, pOff);
      normals.set(p.normals, pOff);
      for (let i = 0; i < p.indices.length; i++) indices[iOff + i] = p.indices[i]! + pOff / 3;
      edgePositions.set(p.edgePositions, eOff);
      pOff += p.positions.length;
      iOff += p.indices.length;
      eOff += p.edgePositions.length;
    }
    return { positions, normals, indices, edgePositions };
  } catch (err) {
    throw new Error(
      `Boolean ${op} failed: ${err instanceof Error ? err.message : err} ` +
        '(geometry may be non-watertight)',
    );
  } finally {
    for (const m of created) m.delete(); // WASM memory is manual
  }
}
